import { useCallback, useMemo, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDispatch } from "react-redux";

import {
  useDeleteConfigurationByID,
  useGetInfiniteConfiguration
} from "@/api/configuration/useConfigurationAPI";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import { ChevLeftIcon, FilterIcon, SpinnerIcon } from "@/assets/icons";
import { Button } from "@/components/ui/button";
import ButtonMenu, { ButtonMenuItem } from "@/components/ui/buttonMenu";
import EmptyData from "@/components/ui/EmptyData";
import SearchBox from "@/components/ui/searchBox";
import { MESSAGE } from "@/constants/message";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useDataset } from "@/hooks/map/useDataset";
import { useConfiguration } from "@/hooks/project/useConfiguration";
import { useDeleteWithToast } from "@/hooks/ui/useDeleteWithToast";
import useHandleClearView from "@/hooks/ui/useHandleClearView";
import {
  mapSpatialState,
  useAnalysisConfiguration
} from "@/hooks/useAnalysisConfiguration";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { useDebounce } from "@/hooks/useDebounce";
import { resetDatasetState } from "@/store/slice/datasetSlice";
import {
  setAdminAreaSettings as setGlobalAdminAreaSettings,
  setAdminAreaSettingsData as setGlobalAdminAreaSettingsData,
  setGlobalAdminArea,
  setSelectedAreaIds as setGlobalSelectedAreaIds
} from "@/store/slice/globalFilterSlice";
import { resetSpatialState, setSpatialState } from "@/store/slice/spatialSlice";
import { setShowTemplateModal } from "@/store/slice/uiSlice";
import {
  AnalysisConfigurationItem,
  AnalysisConfigurationListResponse
} from "@/types/project";

import ProjectItem from "./projectItem";

const TEST_ID_PREFIX = "project-list";

enum ConfigurationFilterType {
  ASC = "ASC",
  DESC = "DESC",
  LATEST_ADDED = "updated_at"
}

type Props = { onGoBack: (data?: AnalysisConfigurationItem) => void };

const DeleteSuccessNotication = ({ name }: { name: string }) => {
  return (
    <div className="flex flex-col gap-2 items-start">
      <p className="text-base text-txtHeading">
        Dataset "{name}" successfully deleted
      </p>
    </div>
  );
};
const FilteredIcon = () => {
  return (
    <Button className="bg-gainsboro h-[40px] btn-secondary w-[40px] !min-w-[40px] btn-normal relative">
      <div className="min-w-[9px] min-h-[9px] rounded-full bg-teal-500 absolute right-0 top-0 z-[999]" />
      <FilterIcon width={24} className="fill-dim-gray" />
    </Button>
  );
};

const ProjectList = ({ onGoBack }: Props) => {
  const dispatch = useDispatch();
  const {
    isTemplateEnabled,
    isRefactorAddDatasetEnabled,
    isAdminTreeEnabled,
    isBVTDataForCatchmentEnabled
  } = useAppFeatures();
  const { addFeature } = useMap();
  const { mutateOpenConfig, configurationDetailData } = useConfiguration({
    onOpenConfigSuccess: (configurationDetail) => {
      if (isRefactorAddDatasetEnabled && configurationDetail?.datasets) {
        // Set data for spatial config
        const spatialData = mapSpatialState(
          configurationDetail,
          addFeature,
          undefined,
          isAdminTreeEnabled,
          isBVTDataForCatchmentEnabled
        );
        dispatch(setSpatialState(spatialData));
        if (isAdminTreeEnabled) {
          if (configurationDetail.globalAdminAreaData?.selectValue?.lv0) {
            dispatch(
              setGlobalAdminAreaSettings({
                lv0: configurationDetail.globalAdminAreaData?.selectValue?.lv0
              })
            );
          }
          dispatch(
            setGlobalAdminAreaSettingsData(
              configurationDetail.globalAdminAreaData?.geoData ?? null
            )
          );
          dispatch(
            setGlobalSelectedAreaIds(configurationDetail.adm_area_ids || [])
          );
        } else {
          dispatch(setGlobalAdminArea(configurationDetail.globalAdminAreaData));
        }

        onGoBack(itemInteractLatest ?? undefined);
      }
    }
  });
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const { handleClearDataset } = useDataset();
  const { handleCloseDetailView } = useHandleClearView();
  const { deleteAllFeatures } = useMap();

  const [itemInteractLatest, setItemInteractLatest] =
    useState<AnalysisConfigurationItem | null>(null);

  const [isFilter, setIsFilter] = useState<boolean>(false);
  const [filterType, setFilterType] = useState<ConfigurationFilterType | null>(
    null
  );
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const debouncedSearchQuery = useDebounce(searchKeyword, 500);

  const { data: userProfile } = useGetUserProfileAPI();

  const getOrderParams = useCallback(() => {
    if (!filterType) return undefined;
    if (filterType === ConfigurationFilterType.LATEST_ADDED)
      return ConfigurationFilterType.DESC;
    return filterType;
  }, [filterType]);

  const memoizedParams = useMemo(
    () => ({
      q: debouncedSearchQuery?.length >= 2 ? debouncedSearchQuery : "",
      order: getOrderParams(),
      sort_by:
        filterType === ConfigurationFilterType.LATEST_ADDED
          ? ConfigurationFilterType.LATEST_ADDED
          : ""
    }),
    [debouncedSearchQuery, filterType, getOrderParams]
  );

  const {
    data: projectData,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage
  } = useGetInfiniteConfiguration(memoizedParams);

  const {
    mutate: mutateDelete,
    isPending: isDeleting,
    isSuccess: deleteSuccess,
    isError: deleteError
  } = useDeleteConfigurationByID();

  useDeleteWithToast({
    isLoading: isDeleting,
    isSuccess: deleteSuccess,
    isError: deleteError,
    loadingMessage: MESSAGE.LOADING,
    successMessage: (
      <DeleteSuccessNotication name={itemInteractLatest?.name ?? ""} />
    ),
    errorMessage: MESSAGE.DELETE_DATASET_FAILED
  });

  // TODO: need to delete after refactor useConfiguration has been tested well
  useAnalysisConfiguration({
    configurationDetail: !isRefactorAddDatasetEnabled
      ? configurationDetailData
      : undefined,
    onGoBack: () => onGoBack(itemInteractLatest ?? undefined)
  });

  const handleFilter = (type: ConfigurationFilterType) => {
    if (type === filterType) {
      setIsFilter(false);
      setFilterType(null);
      scrollContainerRef.current?.scrollTo({ top: 0 });
      return;
    }
    setIsFilter(true);
    setFilterType(type);
    scrollContainerRef.current?.scrollTo({ top: 0 });
  };

  const filterOptions: ButtonMenuItem[] = [
    {
      name: "Accending",
      id: "asc",
      value: ConfigurationFilterType.ASC,
      onClick: () => handleFilter(ConfigurationFilterType.ASC)
    },
    {
      name: "Descending",
      id: "desc",
      value: ConfigurationFilterType.DESC,
      onClick: () => handleFilter(ConfigurationFilterType.DESC)
    },
    {
      name: "Latest added",
      id: "latest",
      value: ConfigurationFilterType.LATEST_ADDED,
      onClick: () => handleFilter(ConfigurationFilterType.LATEST_ADDED)
    }
  ];

  const handleOpenProject = (data: AnalysisConfigurationItem) => {
    handleCloseDetailView();
    // Clear current data before open project
    handleClearDataset();
    deleteAllFeatures();
    dispatch(resetDatasetState());
    dispatch(resetSpatialState());
    mutateOpenConfig({
      id: data.id,
      timezone: userProfile?.timezone
    });
  };

  const handleDeleteProject = (data: AnalysisConfigurationItem) => {
    setItemInteractLatest(data);
    mutateDelete(data.id);
  };

  const projectDataMap =
    projectData?.pages.flatMap(
      (response: AnalysisConfigurationListResponse) => response.data
    ) ?? [];

  return (
    <div className="flex flex-col px-4 overflow-hidden">
      {/* Header */}
      <div className="flex flex-row items-center min-h-[60px] gap-4">
        <button
          data-testid="btn-back"
          className="bg-transparent border-0 p-0"
          onClick={() => onGoBack()}
        >
          <ChevLeftIcon />
        </button>
        <h2 className="text-txtHeading font-sans font-bold text-[20px]">
          Project
        </h2>
        {isTemplateEnabled && (
          <Button
            className="ml-auto text-xs font-semibold rounded-xl"
            variant="outline"
            size="sm"
            onClick={() => dispatch(setShowTemplateModal(true))}
          >
            Template
          </Button>
        )}
      </div>
      {/* Body */}
      <div className="flex flex-col pb-4 overflow-hidden">
        <p className="text-[10px]">
          Access your saved projects from previous spatial analysis.
        </p>
        {/* Filter */}
        <div className="flex mt-2 gap-2">
          <SearchBox
            inputFieldName={`${TEST_ID_PREFIX}-search-project`}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            className="w-full"
            placeHolder="Search"
            testId={TEST_ID_PREFIX}
          />
          <ButtonMenu
            variant="ghost"
            disabled={isLoading || isFetchingNextPage || isDeleting}
            title=""
            icon={
              isFilter ? (
                <FilteredIcon />
              ) : (
                <FilterIcon className="fill-dim-gray" width={24} />
              )
            }
            btnContainerClassName="w-[40px] !min-w-[40px] btn-normal"
            menuClassName="top-[42px] w-[147px] right-0 h-fit z-[99]"
            menu={filterOptions}
            focusItem={filterType}
          />
        </div>
        {/* Display data */}
        <div
          id="scrollableProjectDiv"
          className="flex flex-col flex-1 gap-2 pr-1 mt-4 overflow-y-auto"
          ref={scrollContainerRef}
          data-testid="infinite-project-scrollable"
        >
          {projectDataMap.length > 0 ? (
            <InfiniteScroll
              dataLength={projectDataMap.length}
              next={fetchNextPage}
              hasMore={hasNextPage ?? false}
              loader={null}
              scrollableTarget="scrollableProjectDiv"
              scrollThreshold="95%"
              className="flex flex-col gap-2"
            >
              {projectDataMap.map((item) => (
                <div key={item.id} data-testid="project-item">
                  <ProjectItem
                    key={item.id}
                    data={item}
                    onOpenProject={handleOpenProject}
                    onDeleteProject={handleDeleteProject}
                  />
                </div>
              ))}

              {isFetchingNextPage && (
                <div className="flex flex-row justify-center items-center">
                  <SpinnerIcon
                    className="spin-animation"
                    width={32}
                    height={32}
                    data-testid="loading-spinner"
                  />
                </div>
              )}
            </InfiniteScroll>
          ) : (
            <EmptyData description="You don’t have any saved project." />
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectList;
